import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, MessageSquareMore, MinusCircle, Loader, Paperclip } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

// Concise predefined responses
const responses = {
  greeting: [
      "Hello! I'm your AI ESG assistant. How can I help you today?",
      "Hi there! I'm your AI ESG assistant.Got ESG questions? I'm here to assist you.",
      "Welcome! I'm your AI ESG assistant. What ESG topic interests you today?",
      "Greetings! I'm your AI ESG assistant. I'm here for your ESG inquiries.",
      "Welcome! I'm your AI ESG assistant. How may I assist you today?",
  ],
  esg: "ESG stands for Environmental, Social, and Governance. It's a framework to evaluate a company's sustainability and ethical impact.",
  environmental: "Environmental factors in ESG include climate change, resource use, pollution, and biodiversity. Companies are evaluated on their environmental policies and performance.",
  social: "Social factors in ESG cover employee relations, diversity, working conditions, and community impact. It assesses how a company manages relationships with its workforce, customers, and society.",
  governance: "Governance in ESG refers to a company's leadership, audits, internal controls, and shareholder rights. It ensures accountability, transparency, and ethical business practices.",
  reporting: "ESG reporting discloses a company's sustainability performance using frameworks like GRI or SASB. It helps stakeholders assess non-financial performance and long-term viability.",
  benefits: "Strong ESG practices can lead to better risk management, enhanced reputation, increased customer loyalty, improved employee retention, and potential cost savings through efficiency.",
  default: "I don't have specific information on that, but I can discuss other ESG topics. What aspect interests you most?",
};

const suggestions = [
  "What is ESG?",
  "Environmental factors",
  "Social responsibility",
  "Governance in ESG",
  "ESG benefits",
  "ESG reporting",
];

const AIChatbot = () => {
  const [messages, setMessages] = useState([
    { text: responses.greeting[Math.floor(Math.random() * responses.greeting.length)], isBot: true }
  ]);
  const [input, setInput] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const handleSend = () => {
    if (input.trim() === '') return;
   
    setMessages(prev => [...prev, { text: input, isBot: false }]);
    setInput('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      setIsTyping(false);
      let response = responses.default;
      const lowercaseInput = input.toLowerCase();

      if (lowercaseInput.includes('hi') || lowercaseInput.includes('hello')) {
        response = responses.greeting[Math.floor(Math.random() * responses.greeting.length)];
      } else if (lowercaseInput.includes('esg reporting')) {
        response = responses.reporting;
      } else if (lowercaseInput.includes('esg benefit')) {
        response = responses.benefits;
      } else if (lowercaseInput.includes('esg')) {
        response = responses.esg;
      } else if (lowercaseInput.includes('environmental')) {
        response = responses.environmental;
      } else if (lowercaseInput.includes('social')) {
        response = responses.social;
      } else if (lowercaseInput.includes('governance')) {
        response = responses.governance;
      }

      setMessages(prev => [...prev, { text: response, isBot: true }]);
    }, Math.random() * 500 + 500); // Random delay between 500-1000ms
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    handleSend();
  };

  const handleAttachment = () => {
    console.log("Attachment button clicked");
  };

  return (
    <div className={`fixed bottom-4 right-4 bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col transition-all duration-300 ease-in-out ${isExpanded ? 'w-80 h-[480px]' : 'w-auto h-auto'}`}>
      <div 
        className={`flex items-center justify-between p-3 ${isExpanded ? 'border-b' : 'rounded-lg'} bg-primary text-white cursor-pointer`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2 flex-grow">
          <Bot className="h-5 w-5 flex-shrink-0" />
          <h3 className="font-semibold text-sm truncate">AI ESG Assistant</h3>
        </div>
        <div className="flex-shrink-0 ml-2">
          {isExpanded ? <MinusCircle className="h-5 w-5" /> : <MessageSquareMore className="h-5 w-5" />}
        </div>
      </div>
      {isExpanded && (
        <>
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}>
                <div className={`rounded-lg p-2 max-w-[80%] ${message.isBot ? 'bg-gray-100' : 'bg-primary text-white'}`}>
                  {message.text}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-2">
                  <Loader className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-4 border-t">
  <div className="flex flex-wrap gap-2 mb-2">
    {suggestions.map((suggestion, index) => (
      <Button 
        key={index} 
        variant="outline" 
        size="sm" 
        onClick={() => handleSuggestionClick(suggestion)} 
        className="text-xs py-1 px-2"
      >
        {suggestion}
      </Button>
    ))}
  </div>
  <div className="flex space-x-2 items-center">
    <Button onClick={handleAttachment} size="sm" variant="ghost" className="p-2 w-10">
      <Paperclip className="h-4 w-5" />
    </Button>
    <Input
      value={input}
      onChange={(e) => setInput(e.target.value)}
      placeholder="Type your message..."
      onKeyPress={(e) => e.key === 'Enter' && handleSend()}
      className="flex-grow"
    />
    <Button onClick={handleSend} size="sm" className="p-2 w-12">
      <Send className="h-4 w-5" />
    </Button>
  </div>
</div>
        </>
      )}
    </div>
  );
};

export default AIChatbot;